// Window.webkit is declared in global.d.ts
export const iOSAudioHandler = window.webkit?.messageHandlers.audio;

// NOTE: Load the track for future use and start playing it.
export async function iosAudioPlayFromUrl(url: string): Promise<boolean> {
  return (await iOSAudioHandler?.postMessage({
    method: "play",
    url,
  })) as boolean;
}

/**
 * Whether this shell can stream (`playStream` below). Side-effect free, so it
 * is safe to ask before deciding which URL to hand the player.
 *
 * Shells that predate streaming do not know the method and the handler leaves
 * its result at `false`, which is exactly the answer we want — the caller then
 * waits for the synthesized file instead.
 */
export async function iosAudioCanPlayStream(): Promise<boolean> {
  return (
    (await iOSAudioHandler?.postMessage({ method: "canPlayStream" })) === true
  );
}

/**
 * Play a stream of unknown length (the speech endpoint synthesizes as it
 * sends). The shell fetches the URL itself with `headers`, so the audio never
 * has to become a `blob:` the native player cannot resolve.
 */
export async function iosAudioPlayStream(
  url: string,
  headers: Record<string, string>
): Promise<boolean> {
  return (await iOSAudioHandler?.postMessage({
    method: "playStream",
    url,
    headers,
  })) as boolean;
}

// NOTE: Play a track already loaded with the previous function.
export async function iosAudioPlayResume(): Promise<boolean> {
  return (await iOSAudioHandler?.postMessage({ method: "play" })) as boolean;
}

export async function iosAudioPause(): Promise<boolean> {
  return (await iOSAudioHandler?.postMessage({ method: "pause" })) as boolean;
}

// TODO: The following functions must change to async.

export async function iosAudioStop(): Promise<boolean> {
  return iOSAudioHandler?.postMessage({ method: "stop" }) as boolean;
}

// NOTE: Returns -1 if invalid.
export async function iosAudioGetDuration(): Promise<number> {
  return iOSAudioHandler?.postMessage({ method: "getDuration" }) as number;
}

// NOTE: Returns -1 if invalid.
export async function iosAudioGetCurrentTime(): Promise<number> {
  return iOSAudioHandler?.postMessage({ method: "getCurrentTime" }) as number;
}

// NOTE: Returns -1 if invalid.
export function iosAudioGetPlaybackRate(): number {
  return iOSAudioHandler?.postMessage({ method: "getPlaybackRate" }) as number;
}

export function iosAudioGetPlayerState(): "unstarted" | "playing" | "paused" {
  return iOSAudioHandler?.postMessage({ method: "getPlayerState" }) as
    "unstarted" | "playing" | "paused";
}

export function iosAudioSeek(seconds: number): boolean {
  return iOSAudioHandler?.postMessage({ method: "seek", seconds }) as boolean;
}

export function iosAudioSetPlaybackRate(rate: number): boolean {
  return iOSAudioHandler?.postMessage({
    method: "setPlaybackRate",
    rate,
  }) as boolean;
}
